import { supabase } from './supabaseClient'; // Frontend Supabase client

/**
 * A wrapper around the native fetch function that automatically adds the 
 * Supabase authentication token (JWT) to the request headers if a session exists.
 *
 * @param url The URL to fetch.
 * @param options Optional fetch options (method, body, headers, etc.).
 * @returns A Promise resolving to the Fetch Response object.
 * @throws An error if the fetch operation fails.
 */
export const fetchWithAuth = async (
  url: string | URL | Request,
  options: RequestInit = {}
): Promise<Response> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    const headers = new Headers(options.headers);

    if (session?.access_token) {
      headers.set('Authorization', `Bearer ${session.access_token}`);
      console.log(`fetchWithAuth: Added Authorization header for ${url}`); // Debug log
    } else {
      console.log(`fetchWithAuth: No session token found for ${url}`); // Debug log
      // Depending on policy, you might want to throw an error here if a token is expected
      // For now, allow unauthenticated requests to proceed if no token is found.
    }

    // Ensure Content-Type is set for POST/PUT requests with JSON body
    if (options.body && typeof options.body === 'string') {
        try {
            JSON.parse(options.body);
            if (!headers.has('Content-Type')) {
                headers.set('Content-Type', 'application/json');
            }
        } catch (e) {
            // Not JSON, do nothing
        }
    }

    // Log the final options including the method just before fetch
    const finalOptions = { ...options, headers: headers };
    console.log(`[fetchWithAuth] Making ${finalOptions.method || 'GET'} request to ${url}`);

    // Make the actual fetch call
    const backendUrl = import.meta.env.VITE_BACKEND_API_URL;
    let finalUrl = url;
    
    // Prepend backend URL if it's a relative API path and backendUrl is set
    if (backendUrl && typeof url === 'string' && url.startsWith('/api')) {
      finalUrl = `${backendUrl}${url}`;
    }
    
    const response = await fetch(finalUrl, finalOptions);

    // Optionally: Add global error handling for common HTTP errors (4xx, 5xx)
    // if (!response.ok) {
    //   // Handle specific errors or throw a generic one
    //   console.error(`fetchWithAuth Error: ${response.status} ${response.statusText} for ${url}`);
    //   // Potentially throw new Error(`HTTP error ${response.status}`);
    // }

    return response;

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`fetchWithAuth: Network or other error fetching ${url}. Error: ${errorMessage}`);
    // Re-throw the error to be handled by the caller
    throw error;
  }
}; 